<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import fixWebmDuration from "fix-webm-duration";

import { WAVE_BARS } from "@shared/lib/audioWaveform";
import AppButton from "@shared/ui/components/AppButton.vue";
import AppModal from "@shared/ui/components/AppModal.vue";

import { useFileUpload } from "@features/file-transfer/useFileUpload";

import type { FileAttachment } from "shared";

// Запись аудиосообщения: микрофон → MediaRecorder, вместо камеры — «волны»
// (амплитуда с AnalyserNode рисуется на canvas). Без превью: «Отправить»
// останавливает запись и сразу заливает тем же чанковым транспортом, что файлы.

const props = defineProps<{ chatId: string }>();
const emit = defineEmits<{
    send: [attachment: FileAttachment];
    close: [];
}>();

const { uploadFile } = useFileUpload();

type Stage = "starting" | "recording" | "sending" | "error";
const stage = ref<Stage>("starting");
const errorText = ref("");

const canvasRef = ref<HTMLCanvasElement | null>(null);

let stream: MediaStream | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let mimeType = "";

let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;

let recordedBlob: Blob | null = null;
// Замеренная длительность — идёт в патч webm и attachment.duration (для аудио
// uploadFile метаданные не генерит).
const durationSec = ref(0);

// Таймер записи (отображение) + точный замер для патча webm.
const seconds = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;
let recordStart = 0;

const timeLabel = computed(() => {
    const m = Math.floor(seconds.value / 60);
    const s = seconds.value % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
});

// Таймер записи живёт в заголовке модалки.
const modalTitle = computed(() =>
    stage.value === "recording"
        ? `Аудиосообщение · ${timeLabel.value}`
        : "Аудиосообщение"
);

// Прогресс заливки.
const progress = ref(0);
let controller: AbortController | null = null;

function pickMime(): string {
    const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
    ];
    return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? "";
}

// ── Волны ────────────────────────────────────────────────────────────────
// Во время записи — эквалайзер: неподвижный ряд столбиков на всю ширину,
// высота каждого реагирует на свою полосу спектра (атака мгновенная, спад
// плавный). Параллельно амплитуда копится в history (каждый третий кадр) —
// из неё собирается волна вложения, если декод файла не осилит webm.
const BAR_W = 3;
const GAP = 3;
const PAD = 24;

let history: number[] = [];
let levels: number[] = [];
let raf = 0;
let frame = 0;
let accent = "#b4ff2b";

function setupCanvas(): CanvasRenderingContext2D | null {
    const canvas = canvasRef.value;
    if (!canvas) {
        return null;
    }
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
}

function barCount(): number {
    const w = canvasRef.value?.clientWidth ?? 0;
    return Math.max(1, Math.floor((w - PAD * 2 + GAP) / (BAR_W + GAP)));
}

function drawBars(amps: number[]): void {
    const ctx = setupCanvas();
    const canvas = canvasRef.value;
    if (!ctx || !canvas) {
        return;
    }
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    ctx.clearRect(0, 0, w, h);
    const step = BAR_W + GAP;
    const maxH = h * 0.45;
    ctx.fillStyle = accent;
    amps.forEach((a, i) => {
        const bh = Math.max(2, a * maxH);
        ctx.fillRect(PAD + i * step, h / 2 - bh / 2, BAR_W, bh);
    });
}

function recordLoop(): void {
    if (analyser) {
        const count = barCount();
        if (levels.length !== count) {
            levels = new Array(count).fill(0);
        }
        // Спектр зеркально от центра: низкие частоты (голос) — в середине,
        // высокие — по краям. Верхние ~40% бинов почти всегда пустые —
        // отбрасываем.
        const freq = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(freq);
        const usable = Math.floor(freq.length * 0.6);
        const half = (count - 1) / 2;
        for (let i = 0; i < count; i++) {
            // Доля удаления от центра (0 в середине, 1 у краёв) → полоса частот.
            const dist = half > 0 ? Math.abs(i - half) / half : 0;
            const from = Math.floor(dist * (usable - 1));
            const to = Math.max(
                from + 1,
                Math.ceil((dist + 1 / count) * usable)
            );
            let peak = 0;
            for (let j = from; j < to; j++) {
                peak = Math.max(peak, freq[j]);
            }
            const target = peak / 255;
            // Атака мгновенная, спад плавный — столбики «дышат», не мерцают.
            levels[i] = target > levels[i] ? target : levels[i] * 0.85;
        }
        // Амплитуда — в history, из неё потом собирается волна превью.
        if (frame++ % 3 === 0) {
            const buf = new Uint8Array(analyser.fftSize);
            analyser.getByteTimeDomainData(buf);
            let amp = 0;
            for (const v of buf) {
                amp = Math.max(amp, Math.abs(v - 128));
            }
            history.push(Math.min(1, (amp / 128) * 1.6));
        }
        drawBars(levels);
    }
    raf = requestAnimationFrame(recordLoop);
}

function stopLoop(): void {
    if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
    }
}

// Свёртка записанной history в count пиков (max по корзине) + нормализация,
// чтобы волна заполняла высоту независимо от громкости микрофона. Диапазон
// сжимается корнем (амплитуда → на глаз ближе к громкости): тихие места
// подтягиваются, пики не так отрываются; плюс минимальная высота столбика.
const PEAK_FLOOR = 0.18;

function buildPreviewPeaks(count = barCount()): number[] {
    if (!history.length) {
        return new Array(count).fill(0);
    }
    const peaks: number[] = [];
    for (let i = 0; i < count; i++) {
        const from = Math.floor((i * history.length) / count);
        const to = Math.max(
            from + 1,
            Math.floor(((i + 1) * history.length) / count)
        );
        peaks.push(Math.max(...history.slice(from, to)));
    }
    const max = Math.max(...peaks, 0.01);
    return peaks.map((p) => Math.max(PEAK_FLOOR, Math.sqrt(p / max)));
}

// ── Запись ───────────────────────────────────────────────────────────────

async function start(): Promise<void> {
    stage.value = "starting";
    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
        stage.value = "error";
        // Имя исключения — на экран: NotAllowedError = запрет разрешений
        // (TCC/webview), TypeError = нет mediaDevices (небезопасный контекст),
        // NotFoundError = нет устройства.
        const detail =
            e instanceof Error ? `${e.name}: ${e.message}` : String(e);
        errorText.value = `Микрофон недоступен (${detail})`;
        return;
    }
    audioCtx = new AudioContext();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    audioCtx.createMediaStreamSource(stream).connect(analyser);

    mimeType = pickMime();
    chunks = [];
    history = [];
    frame = 0;
    recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 128_000,
    });
    recorder.ondataavailable = (e) => {
        if (e.data.size) {
            chunks.push(e.data);
        }
    };
    recorder.onstop = async () => {
        let blob = new Blob(chunks, { type: mimeType || "audio/webm" });
        const measured = performance.now() - recordStart;
        durationSec.value = Math.max(1, Math.round(measured / 1000));
        // Chromium-баг: у webm из MediaRecorder нет длительности (Infinity,
        // кривой сик) — патчим Duration в EBML-заголовок замеренным временем.
        if (blob.type.includes("webm")) {
            try {
                blob = await fixWebmDuration(blob, measured, {
                    logger: false,
                });
            } catch {
                // Оставляем как есть — просто без метаданных длительности.
            }
        }
        recordedBlob = blob;
        stopStream();
        stopLoop();
        // Без превью — сразу заливаем и шлём.
        void send();
    };
    recorder.start();
    recordStart = performance.now();
    seconds.value = 0;
    timer = setInterval(() => seconds.value++, 1000);
    stage.value = "recording";
    recordLoop();
}

function stopTimer(): void {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

function stopStream(): void {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    analyser = null;
    void audioCtx?.close().catch(() => {});
    audioCtx = null;
}

function stopRecording(): void {
    stopTimer();
    if (recorder && recorder.state !== "inactive") {
        // Заливка стартует в recorder.onstop (send).
        recorder.stop();
    }
}

async function send(): Promise<void> {
    if (!recordedBlob || stage.value === "sending") {
        return;
    }
    stage.value = "sending";
    progress.value = 0;
    controller = new AbortController();
    const type = mimeType || "audio/webm";
    const ext = type.includes("mp4")
        ? "m4a"
        : type.includes("ogg")
          ? "ogg"
          : "webm";
    const file = new File(
        [recordedBlob],
        `voice-message-${Date.now()}.${ext}`,
        { type }
    );
    try {
        const attachment = await uploadFile(
            file,
            props.chatId,
            (fraction) => {
                progress.value = fraction;
            },
            controller.signal
        );
        // Помечаем как голосовое — лента рендерит его иначе, чем файл.
        attachment.kind = "voice";
        attachment.duration = durationSec.value;
        // Волну обычно генерит uploadFile декодом файла; если декод не осилил
        // наш webm — собираем из амплитуд, накопленных при записи.
        if (!attachment.waveform?.length) {
            attachment.waveform = buildPreviewPeaks(WAVE_BARS).map(
                (v) => Math.round(v * 100) / 100
            );
        }
        emit("send", attachment);
        cleanup();
        emit("close");
    } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") {
            return;
        }
        stage.value = "error";
        // Причина — на экран: в релизной сборке консоли нет.
        const detail = e instanceof Error ? e.message : String(e);
        errorText.value = `Не удалось загрузить аудио: ${detail}`;
    }
}

function cleanup(): void {
    stopTimer();
    stopLoop();
    // Отмена активной заливки подчистит недокачанный файл на сервере.
    controller?.abort();
    controller = null;
    if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        recorder.stop();
    }
    recorder = null;
    stopStream();
    recordedBlob = null;
}

function cancel(): void {
    cleanup();
    emit("close");
}

onMounted(() => {
    // Цвет волн — из темы (canvas не умеет var()).
    accent =
        getComputedStyle(document.documentElement)
            .getPropertyValue("--mc-acid")
            .trim() || accent;
    void start();
});
onBeforeUnmount(cleanup);
</script>

<template>
    <AppModal
        :is-visible="true"
        :title="modalTitle"
        :max-width="420"
        no-padding
        @close="cancel"
    >
        <div class="mc-arec">
            <div class="mc-arec__screen">
                <!-- Эквалайзер записи (в заливке остаётся последний кадр). -->
                <canvas ref="canvasRef" class="mc-arec__canvas" />
                <div v-if="stage === 'error'" class="mc-arec__error">
                    {{ errorText }}
                </div>

                <!-- Одна кнопка поверх волн: останавливает запись и сразу
                     шлёт (во время заливки — прыгающие точки). -->
                <div
                    v-if="stage === 'recording' || stage === 'sending'"
                    class="mc-arec__overlay-actions"
                >
                    <AppButton
                        variant="pill"
                        :loading="stage === 'sending'"
                        @click="stopRecording"
                    >
                        Отправить
                    </AppButton>
                </div>
            </div>

            <!-- Все действия лежат на экране; отмена — закрытие модалки
                 (Esc / ×). Ряд под экраном остаётся только для ошибки. -->
            <div v-if="stage === 'error'" class="mc-arec__actions">
                <AppButton variant="ghost" @click="cancel">Закрыть</AppButton>
            </div>
        </div>
    </AppModal>
</template>

<style scoped lang="scss">
.mc-arec {
    display: flex;
    flex-direction: column;
    gap: 14px;

    &__screen {
        position: relative;
        // Тот же квадрат, что у записи видео.
        aspect-ratio: 1 / 1;
        background: #000;
        border-bottom: 1px solid var(--mc-line);
        overflow: hidden;
    }

    &__canvas {
        width: 100%;
        height: 100%;
        display: block;
    }

    &__error {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--mc-danger);
        font-family: var(--mc-mono);
        font-size: 12px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    // Кнопки поверх волн — снизу по центру.
    &__overlay-actions {
        position: absolute;
        bottom: 18px;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        gap: 8px;
    }

    // Ряд под экраном (ошибка): свои отступы — модалка без паддингов.
    &__actions {
        display: flex;
        gap: 8px;
        padding: 0 16px 16px;
    }
}
</style>
